// CRM-based exclusion helpers (FE-E — People-search CRM exclusion filters).
//
// Bridges the SearchFilters CRM-exclusion toggles to the frozen
// /crm/exclusion-set backend contract and back into the Crustdata exclude
// inputs. Kept out of ``people-search-hook`` so the mapping is a small,
// self-contained unit that the later cross-PR merge can move/extend
// without dragging the hook's RTK/mutation graph along.

import { normalizeDomainList } from '@/lib/normalize-domain';
import type {
  CrmExclusionCondition,
  CrmExclusionIdentifiers,
} from '@/store/features/crm.feature';
import type { SearchFilters } from '@/types/search-filters';
import { crmStatusIdentifierKey } from '@/utils/crm-status-identifier.util';

// ─── Toggle → condition-token mapping ───────────────────────────────

/** One CRM exclusion the user has enabled, paired with the backend
 *  condition token to resolve it. ``key`` is the SearchFilters field —
 *  used as a stable id for per-condition UI state (freshness note,
 *  degrade-to-disabled) and to clear the toggle on a hard 400. */
export interface ActiveCrmExclusion {
  key: keyof SearchFilters;
  condition: CrmExclusionCondition;
}

/**
 * Resolve the set of enabled CRM exclusions from the current filters,
 * each mapped to its `/crm/exclusion-set` condition token.
 *
 * The four boolean toggles map to fixed tokens; the two parameterized
 * fields template the value into ``owner:<crm_user_id>`` /
 * ``account_status:<status>`` and are skipped when blank.
 *
 * ``owned_by_me`` and ``owned_by_others`` are mutually exclusive in the
 * UI, but this function does not enforce that — it just emits whatever is
 * enabled. The sidebar disables one when the other is on.
 */
export function getActiveCrmExclusions(
  filters: SearchFilters
): ActiveCrmExclusion[] {
  const active: ActiveCrmExclusion[] = [];
  if (filters.excludeCrmCustomers) {
    active.push({ key: 'excludeCrmCustomers', condition: 'current_customers' });
  }
  if (filters.excludeCrmActiveOpportunities) {
    active.push({
      key: 'excludeCrmActiveOpportunities',
      condition: 'active_opportunities',
    });
  }
  if (filters.excludeCrmOwnedByMe) {
    active.push({ key: 'excludeCrmOwnedByMe', condition: 'owned_by_me' });
  }
  if (filters.excludeCrmOwnedByOthers) {
    active.push({
      key: 'excludeCrmOwnedByOthers',
      condition: 'owned_by_others',
    });
  }
  const owner = filters.excludeCrmOwner.trim();
  if (owner) {
    active.push({ key: 'excludeCrmOwner', condition: `owner:${owner}` });
  }
  const status = filters.excludeCrmAccountStatus.trim();
  if (status) {
    active.push({
      key: 'excludeCrmAccountStatus',
      condition: `account_status:${status}`,
    });
  }
  return active;
}

// ─── Identifier merge ───────────────────────────────────────────────

/** Empty identifier accumulator. */
export function emptyCrmIdentifiers(): CrmExclusionIdentifiers {
  return { domains: [], linkedin_urls: [], names: [] };
}

/**
 * Merge several exclusion-set identifier payloads into one de-duplicated
 * set. Multiple enabled conditions (e.g. "current customers" AND "active
 * opportunities") union their identifiers — anyone matching ANY enabled
 * condition is suppressed. Dedupe is case-insensitive on a normalized key
 * but the original casing of the first occurrence is preserved (Crustdata
 * equality on names is case-sensitive, but we don't want two casings of
 * the same value to both be sent).
 */
export function mergeCrmIdentifiers(
  payloads: ReadonlyArray<CrmExclusionIdentifiers>
): CrmExclusionIdentifiers {
  const dedupe = (lists: ReadonlyArray<ReadonlyArray<string>>): string[] => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const list of lists) {
      for (const raw of list) {
        if (typeof raw !== 'string') continue;
        const trimmed = raw.trim();
        if (!trimmed) continue;
        const norm = trimmed.toLowerCase();
        if (seen.has(norm)) continue;
        seen.add(norm);
        out.push(trimmed);
      }
    }
    return out;
  };
  return {
    domains: dedupe(payloads.map((p) => p.domains ?? [])),
    linkedin_urls: dedupe(payloads.map((p) => p.linkedin_urls ?? [])),
    names: dedupe(payloads.map((p) => p.names ?? [])),
  };
}

// ─── LinkedIn exclude-URL variant expansion ─────────────────────────

/**
 * [PR1 identity-canonicalization-ssot] Expand each LinkedIn person-profile URL
 * into the host + trailing-slash variants of its CANONICAL (identity SSOT) form
 * so a Crustdata ``flagship_profile_url not_in`` denylist matches Crustdata's
 * own stored vanity URL regardless of which host/slash shape Crustdata holds.
 *
 * The internal divergence this helper used to paper over (our exclusion-set
 * endpoint vs the Cleanlist-DB endpoint emitting different shapes) is GONE: the
 * BE now emits ONE canonical form (`canonical_linkedin_url`:
 * ``https://linkedin.com/in/<slug>`` — no ``www``, no trailing slash, **slug
 * lowercased**) from every endpoint, and the FE mirrors lowercase the slug too.
 * The ONLY remaining reason to spray variants is the EXTERNAL system: Crustdata
 * matches ``not_in`` by exact string against ITS stored shape, which we do not
 * control and which commonly carries ``www`` and/or a trailing slash. Emitting
 * both host and both trailing-slash variants of the canonical, **lowercased**
 * slug is strictly ADDITIVE on a denylist and closes the slug-case leak
 * (copilot-9: ``/in/Jane-Doe`` previously leaked past a ``/in/jane-doe``
 * denylist because the slug case was preserved).
 *
 * Non-person URLs (no ``/in/`` or ``/pub/``) and unparseable strings are dropped
 * (they cannot identify a person and only add noise). Output is de-duplicated
 * with stable order.
 */
export function expandLinkedInExcludeVariants(
  urls: ReadonlyArray<string>
): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  const push = (u: string) => {
    if (!seen.has(u)) {
      seen.add(u);
      out.push(u);
    }
  };
  for (const raw of urls) {
    if (typeof raw !== 'string') continue;
    // Canonicalize through the identity-SSOT normalizer (strict LinkedIn host
    // check, lowercased slug, scheme-less input handled) instead of a loose
    // substring regex — `fakelinkedin.com/in/x` must NOT survive into the
    // denylist. Non-person / non-LinkedIn / junk input canonicalizes to null
    // and is dropped.
    const key = crmStatusIdentifierKey(raw);
    if (!key) continue;
    // Canonical form is `https://linkedin.com/<verb>/<slug>`; derive the
    // host + trailing-slash variants from that path.
    const personPath = key.slice('https://linkedin.com/'.length);
    for (const host of ['https://www.linkedin.com', 'https://linkedin.com']) {
      push(`${host}/${personPath}`);
      push(`${host}/${personPath}/`);
    }
  }
  return out;
}

// ─── Crustdata exclude-condition builder ────────────────────────────

type FilterCondition = Record<string, unknown>;

/**
 * Translate a merged CRM identifier set into Crustdata exclude
 * conditions, mirroring the existing exclude plumbing in
 * ``buildCrustdataFilters``:
 *
 *  - LinkedIn URLs   → the prospect's OWN profile URL, so we exclude on
 *                      ``flagship_profile_url`` (``not_in``) — the same
 *                      vanity-URL column the positive URL lookup filters on,
 *                      exactly like the manual "Exclude specific URLs" field.
 *                      (``linkedin_profile_url`` is response-only and a
 *                      ``not_in`` on it matches nobody — the P1 502 bug.)
 *  - Company domains → ``current_employers.company_website_domain``
 *                      (``not_in``) — suppress anyone currently employed
 *                      at an excluded account's domain.
 *  - Names           → NOT emitted. Crustdata person-search has no
 *                      full-name filter column (``full_name`` is response-
 *                      only), so a ``not_in`` on it excludes nobody. Name-
 *                      only records can't be wire-excluded (gap surfaced
 *                      to the user upstream).
 *
 * Each non-empty channel becomes one ``not_in`` condition (an AND-level
 * exclusion alongside the user's other filters). Returns the array of
 * conditions to push into the top-level AND group; empty when the
 * identifier set is empty.
 */
export function buildCrmExcludeConditions(
  identifiers: CrmExclusionIdentifiers
): FilterCondition[] {
  const conditions: FilterCondition[] = [];

  // Person LinkedIn URLs. These are the prospect's own profile URLs, so
  // they exclude on ``flagship_profile_url`` exactly like the manual
  // "Exclude specific URLs" field and the positive URL lookup do. We do NOT
  // run them through the company-URL normalizer (that rejects /in/ person
  // paths). They arrive in the CRM endpoint's aggressive canonical form (no
  // ``www``/trailing slash), so expand to the host/slash variants the
  // Cleanlist-DB + manual sources use — additive on a denylist, so the
  // divergence can't silently exclude nobody.
  const linkedinUrls = expandLinkedInExcludeVariants(
    identifiers.linkedin_urls.filter(
      (u) => typeof u === 'string' && u.trim().length > 0
    )
  );
  if (linkedinUrls.length > 0) {
    conditions.push({
      column: 'flagship_profile_url',
      type: 'not_in',
      value: linkedinUrls,
    });
  }

  // Company website domains — suppress people at excluded accounts.
  // CRM records store whatever a rep typed ("https://Acme.com/"). A URL-shaped
  // value never matches this bare-hostname column, so it would silently fail to
  // exclude the account — normalize rather than just lowercase.
  const domains = normalizeDomainList(identifiers.domains);
  if (domains.length > 0) {
    conditions.push({
      column: 'current_employers.company_website_domain',
      type: 'not_in',
      value: domains,
    });
  }

  // Contact/account names are intentionally NOT emitted as a Crustdata
  // condition: the person-search schema has NO full-name filter column
  // ('full_name' is response-only), so a {column:'full_name',type:'not_in'}
  // clause silently matches zero — the search would *look* filtered while
  // excluding nobody. Name-only CRM records (no LinkedIn URL, no domain)
  // therefore can't be excluded at the wire; that gap is surfaced upstream.
  // Guarded by the Crustdata column allow-list contract test.

  return conditions;
}
